import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { X, Cookie, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('tp_cookie_consent');
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = { necessary: true, analytics: true, marketing: true, timestamp: new Date().toISOString() };
    localStorage.setItem('tp_cookie_consent', JSON.stringify(allConsent));
    setVisible(false);
  };

  const handleAcceptSelected = () => {
    const consent = { ...preferences, timestamp: new Date().toISOString() };
    localStorage.setItem('tp_cookie_consent', JSON.stringify(consent));
    setVisible(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const minConsent = { necessary: true, analytics: false, marketing: false, timestamp: new Date().toISOString() };
    localStorage.setItem('tp_cookie_consent', JSON.stringify(minConsent));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4"
        style={{ animation: 'slideUp 0.4s ease' }}>
        <div className="max-w-5xl mx-auto rounded-lg shadow-2xl border border-gray-700 p-4 sm:p-5"
          style={{ backgroundColor: 'rgba(26,26,28,0.97)', backdropFilter: 'blur(10px)' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie size={20} className="text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm leading-relaxed">
                  Utilizziamo i cookie per offrirti la miglior esperienza possibile sul nostro sito web. Accettando, accetti l'utilizzo di tali cookie.{' '}
                  <Link to="/privacy" className="text-red-400 hover:text-red-300 underline transition-colors"
                    onClick={() => setVisible(false)}>
                    Informativa sulla privacy
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-shrink-0">
              <button
                onClick={() => setShowSettings(true)}
                className="px-3 py-2 text-xs sm:text-sm text-gray-400 border border-gray-600 rounded hover:bg-gray-800 hover:text-white transition-colors whitespace-nowrap"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Impostazioni
              </button>
              <button
                onClick={handleRejectAll}
                className="px-3 py-2 text-xs sm:text-sm text-gray-400 border border-gray-600 rounded hover:bg-gray-800 hover:text-white transition-colors whitespace-nowrap"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Rifiuta
              </button>
              <Button
                onClick={handleAcceptAll}
                className="px-4 sm:px-6 py-2 text-xs sm:text-sm text-white font-bold whitespace-nowrap"
                style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}
              >
                Accetta
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <Shield size={20} className="text-red-500" />
              IMPOSTAZIONI COOKIE
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-5">
            <p className="text-gray-400 text-sm leading-relaxed">
              Scegli quali cookie desideri accettare. I cookie necessari non possono essere disattivati.
            </p>

            {/* Necessary */}
            <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
              <div>
                <p className="text-white text-sm font-medium" style={{ fontFamily: 'Oswald, sans-serif' }}>Cookie necessari</p>
                <p className="text-gray-400 text-xs mt-0.5">Essenziali per il funzionamento del sito</p>
              </div>
              <Switch checked={true} disabled className="opacity-50" />
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
              <div>
                <p className="text-white text-sm font-medium" style={{ fontFamily: 'Oswald, sans-serif' }}>Cookie analitici</p>
                <p className="text-gray-400 text-xs mt-0.5">Ci aiutano a capire come utilizzi il sito</p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(val) => setPreferences(p => ({ ...p, analytics: val }))}
              />
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
              <div>
                <p className="text-white text-sm font-medium" style={{ fontFamily: 'Oswald, sans-serif' }}>Cookie di marketing</p>
                <p className="text-gray-400 text-xs mt-0.5">Utilizzati per mostrarti contenuti pertinenti</p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(val) => setPreferences(p => ({ ...p, marketing: val }))}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleAcceptSelected}
                className="flex-1 text-white font-bold text-sm"
                style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}>
                SALVA PREFERENZE
              </Button>
              <Button onClick={handleAcceptAll} variant="outline"
                className="flex-1 border-gray-600 text-white hover:bg-gray-800 text-sm"
                style={{ fontFamily: 'Oswald, sans-serif' }}>
                ACCETTA TUTTI
              </Button>
            </div>

            <p className="text-gray-500 text-xs text-center">
              <Link to="/privacy" className="hover:text-gray-300 underline" onClick={() => setShowSettings(false)}>
                Leggi l'informativa completa sulla privacy
              </Link>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default CookieConsent;
